import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Modal,
  Button,
  Spinner,
  ProgressBar,
  Form,
  Card,
  Badge,
} from "react-bootstrap";
import {
  getDriveSelectedDate,
  uploadToServer,
  uploadToDrive,
  checkFile,
} from "../../api/drive-service";
import { toast } from "../../helpers/swal";
import { getDateDayMountYear } from "../../helpers/functions/date-time";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";

const EMB_PARENT_FOLDER_ID = "1ArBT61fWRFwh4iqpgzTl-hHdQ8WnSbGn";

const sanitizeText = (txt) =>
  (txt || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .trim();

const truncate = (str, max = 30) =>
  str && str.length > max ? str.substring(0, max - 3) + "..." : str || "";

// Single AI file per order (extension filter)
const ALLOWED_ORDER_FILE_EXT = ".ai";

const EmbroideryUploadModal = ({ show, onClose, order }) => {
  // states
  const [folders, setFolders] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [uploadingItems, setUploadingItems] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedDesigns, setUploadedDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);

  // Single order-level AI file state
  const [orderFile, setOrderFile] = useState(null);
  const [orderUploading, setOrderUploading] = useState(false);
  const [orderUploadProgress, setOrderUploadProgress] = useState(null);
  const [selectedOrderFile, setSelectedOrderFile] = useState(null);

  const [isReady, setIsReady] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initError, setInitError] = useState(null);

  // local authoritative order (fetched from server)
  const [localOrder, setLocalOrder] = useState(null);

  // layout constants
  const MAX_MODAL_WIDTH = 1400;
  const ITEM_IMG_SIZE = 90;
  const REGION_MIN_WIDTH = 150;

  // activeOrder: prefer server-fetched localOrder; fallback to prop order
  const activeOrder = localOrder || order;

  useEffect(() => {
    if (show) initModal();
    else {
      resetStates();
      setIsReady(false);
      setInitializing(false);
      setInitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, order]);

  // Pick the order-level AI file (if any) whenever designs or order change
  useEffect(() => {
    const ai = (uploadedDesigns || []).find(
      (d) =>
        (d.file_name || "").toLowerCase().endsWith(ALLOWED_ORDER_FILE_EXT) ||
        (d.position || "").toLowerCase().includes("ai") ||
        (d.position || "").toLowerCase().includes("order")
    );
    setOrderFile(ai || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedDesigns, localOrder, order]);

  const initModal = async () => {
    resetStates();
    setIsReady(false);
    setInitError(null);
    setInitializing(true);

    try {
      const ok = await ensureGoogleApisTokenWithRetry(3, 1000);
      if (!ok) {
        setInitError(
          "Google API token alınamadı. İnternet bağlantısını / sunucu yanıtını kontrol edin."
        );
        setInitializing(false);
        return;
      }

      await fetchFolders();

      // Always fetch authoritative order details from server when modal opens
      if (order && (order.orderId || order.orderNumber)) {
        await fetchOrderDetails(order.orderId, order.orderNumber);
      }

      // Fetch designs (this will update uploadedDesigns -> distribute)
      await fetchOrderDesigns();

      setIsReady(true);
    } catch (err) {
      setInitError("Başlatma sırasında hata oluştu.");
    } finally {
      setInitializing(false);
    }
  };

  const ensureGoogleApisTokenWithRetry = async (
    maxRetries = 3,
    delay = 1000
  ) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const existingToken = sessionStorage.getItem("googleApisToken");
        if (existingToken) return true;
        const response = await axios.get(
          `${settings.apiURL}/generateApiToken.php`
        );
        if (response.data?.access_token) {
          sessionStorage.setItem("googleApisToken", response.data.access_token);
          if (response.data.refresh_token)
            sessionStorage.setItem(
              "googleRefreshToken",
              response.data.refresh_token
            );
          return true;
        } else {
          await new Promise((r) => setTimeout(r, delay));
        }
      } catch (err) {
        if (attempt < maxRetries)
          await new Promise((r) => setTimeout(r, delay * attempt));
      }
    }
    return false;
  };

  const resetStates = () => {
    setSelectedDate("");
    setFilteredFolders([]);
    setSelectedFolderId("");
    setSelectedFolderName("");
    setUploadingItems({});
    setUploadProgress({});
    setUploadedDesigns([]);
    setSelectedOrderFile(null);
    setOrderFile(null);
    setOrderUploading(false);
    setOrderUploadProgress(null);
    setLocalOrder(null);
  };

  const fetchFolders = async () => {
    try {
      const res = await getDriveSelectedDate(EMB_PARENT_FOLDER_ID);
      let list = [];
      if (Array.isArray(res?.data)) list = res.data;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.folders)) list = res.data.folders;
      else if (res && res.data && typeof res.data === "object") {
        const vals = Object.values(res.data || {});
        if (Array.isArray(vals)) list = vals.filter(Boolean);
      }
      setFolders(list);
      if (selectedDate) {
        const formattedDate = getDateDayMountYear(selectedDate);
        const matchingFolder = list.find((f) => f && f.name === formattedDate);
        if (matchingFolder) {
          try {
            const resp = await getDriveSelectedDate(matchingFolder.id);
            const embFolders = Array.isArray(resp.data)
              ? resp.data
              : resp.data?.data || resp.data?.folders || [];
            const embFolder = embFolders.find(
              (f) => f?.name && f.name.toLowerCase().includes("embroid")
            );
            if (embFolder) {
              setFilteredFolders([embFolder]);
              setSelectedFolderId(embFolder.id);
              setSelectedFolderName(embFolder.name);
            }
          } catch (err) {
            // ignore
          }
        }
      }
    } catch (err) {
      toast("Failed to fetch folders", "error");
      setFolders([]);
      setFilteredFolders([]);
      setSelectedFolderId("");
      setSelectedFolderName("");
    }
  };

  const fetchOrderDetails = async (orderIdParam, orderNumberParam) => {
    try {
      const id = orderIdParam || "";
      const source = order?.source || "maya";
      const q = id
        ? `orderId=${encodeURIComponent(id)}`
        : orderNumberParam
        ? `orderNumber=${encodeURIComponent(orderNumberParam)}`
        : "";
      if (!q) return;
      const resp = await axios.get(
        `${settings.apiURL}/embroidery_get_order_details.php?${q}&source=${source}`,
        { headers: authHeader() }
      );
      if (resp.data?.success) {
        setLocalOrder(resp.data.order);
        // if backend returned top-level designs, use them as authoritative starting set
        setUploadedDesigns(resp.data.designs || []);
      }
    } catch (err) {
      // ignore silently (UI still tries with prop order)
    }
  };

  const fetchOrderDesigns = async () => {
    const ord = localOrder || order;
    const orderNumber = ord?.orderNumber;
    if (!orderNumber) return;
    setLoadingDesigns(true);
    try {
      const resp = await axios.get(
        `${settings.apiURL}/embroidery_get_order_designs.php?orderNumber=${orderNumber}`,
        { headers: authHeader() }
      );
      if (resp.data?.success) {
        const designs = resp.data.designs || [];
        setUploadedDesigns(designs);
      } else {
        setUploadedDesigns([]);
        setOrderFile(null);
      }
    } catch (err) {
      setUploadedDesigns([]);
      setOrderFile(null);
    } finally {
      setLoadingDesigns(false);
    }
  };

  // Per-item distribution removed — single AI file per order is used now.

  // Helper to read color/size options from item
  const getOptionValue = (item, keys = []) => {
    if (!item || !Array.isArray(item.options)) return null;
    for (const opt of item.options) {
      if (!opt || !opt.name) continue;
      const name = String(opt.name).toLowerCase().trim();
      for (const k of keys) {
        if (name === k.toLowerCase()) {
          return opt.value ?? null;
        }
      }
    }
    return null;
  };

  const handleDateChange = async (e) => {
    const dateValue = e.target.value;
    setSelectedDate(dateValue);
    const formattedDate = getDateDayMountYear(dateValue);
    const folderList = Array.isArray(folders) ? folders : [];
    const matchingFolder = folderList.find(
      (folder) => folder && folder.name === formattedDate
    );
    if (matchingFolder) {
      try {
        const resp = await getDriveSelectedDate(matchingFolder.id);
        const embFolders = Array.isArray(resp.data)
          ? resp.data
          : resp.data?.data || resp.data?.folders || [];
        const embFolder = embFolders.find(
          (f) => f?.name && f.name.toLowerCase().includes("embroid")
        );
        if (embFolder) {
          setFilteredFolders([embFolder]);
          setSelectedFolderId(embFolder.id);
          setSelectedFolderName(embFolder.name);
        } else {
          setFilteredFolders([]);
          setSelectedFolderId("");
          setSelectedFolderName("");
          toast("No matching embroidery folder found for this date", "warning");
        }
      } catch (err) {
        toast("Failed to fetch subfolders", "error");
        setFilteredFolders([]);
        setSelectedFolderId("");
        setSelectedFolderName("");
      }
    } else {
      setFilteredFolders([]);
      setSelectedFolderId("");
      setSelectedFolderName("");
      if (dateValue) toast("No matching folder found for this date", "warning");
    }
  };

  const handleRegionButtonClick = (itemIndex, regionKey) => {
    toast(
      "Per-item uploads are deprecated. Use the order-level .ai upload.",
      "warning"
    );
    return;
  };

  const getDesignsForItem = (item, idx) => {
    return []; // per-item designs deprecated
  };

  const handleRegionFileSelected = (item, itemIndex, regionKey, file) => {
    toast(
      "Per-item uploads are deprecated. Use the order-level .ai upload.",
      "warning"
    );
    return;
  };

  const getItemImageUrl = (item) => {
    return (
      item.imageUrl ||
      item.image ||
      item.image_url ||
      item.thumbnail ||
      (item.images &&
        item.images[0] &&
        (item.images[0].src || item.images[0].url)) ||
      (item.images && typeof item.images === "string" ? item.images : "") ||
      ""
    );
  };

  const filteredItems = (orderObj) =>
    orderObj?.items?.filter(
      (itm) =>
        itm.name && !["discount", "shipping"].includes(itm.name.toLowerCase())
    ) || [];

  // Order-level upload handlers
  const handleOrderFileClick = () => {
    const input = document.getElementById("file-order-ai");
    if (input) input.click();
  };

  const handleOrderFileSelected = (file) => {
    if (!file) return;
    setSelectedOrderFile(file);
    handleUploadForOrder(file);
  };

  const handleUploadForOrder = async (file) => {
    if (!file) {
      toast("Please select a file to upload", "warning");
      return;
    }
    if (!selectedFolderId) {
      toast("Please select date and folder", "warning");
      return;
    }
    if (!file.name.toLowerCase().endsWith(ALLOWED_ORDER_FILE_EXT)) {
      toast("Only .ai files are allowed for order-level upload", "warning");
      return;
    }

    setOrderUploading(true);
    setOrderUploadProgress(0);

    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(
        currentProgress + Math.floor(Math.random() * 10) + 5,
        95
      );
      setOrderUploadProgress(currentProgress);
      if (currentProgress >= 95) clearInterval(progressInterval);
    }, 400);

    try {
      const user_id = localStorage.getItem("user_id");
      const formattedDate = getDateDayMountYear(selectedDate);
      const selectedFolderNameLocal =
        selectedFolderName ||
        filteredFolders.find((f) => f.id === selectedFolderId)?.name ||
        "EMBROIDERY";

      const ord = localOrder || order;
      const orderNumber = ord?.orderNumber || "order";
      const originalName = file.name || "";
      const extension = originalName.includes(".")
        ? originalName.substring(originalName.lastIndexOf("."))
        : ALLOWED_ORDER_FILE_EXT;
      const baseName = originalName.includes(".")
        ? originalName.substring(0, originalName.lastIndexOf("."))
        : originalName;
      // sanitize baseName to keep file system friendly (uses sanitizeText helper)
      const safeBase = sanitizeText(baseName) || "design";

      // Store readable filename that still contains a timestamp for uniqueness
      const renamedFileName = `${orderNumber}__ai__${safeBase}__${Date.now()}${extension}`;
      const renamedFile = new File([file], renamedFileName, {
        type: file.type,
      });

      const resp = await uploadToServer(
        user_id,
        selectedFolderNameLocal,
        renamedFile,
        formattedDate
      );
      const temp_path = resp.data.response.temp_path;
      const temp_id = resp.data.response.temp_id;

      const driveResp = await uploadToDrive(
        temp_id,
        temp_path,
        selectedFolderId,
        selectedFolderNameLocal
      );
      const fileId = driveResp.data.file_id;

      await checkFile(temp_id, fileId, temp_path);

      const saveResp = await axios.post(
        `${settings.apiURL}/embroidery_upload_design.php`,
        {
          orderNumber: orderNumber,
          fileId: fileId,
          fileName: renamedFileName,
          position: "order_ai",
        },
        { headers: authHeader() }
      );

      clearInterval(progressInterval);
      setOrderUploadProgress(100);

      const returnedDesign = saveResp.data?.design;
      const returnedFileId = returnedDesign?.file_id || fileId;
      setOrderFile(
        returnedDesign || {
          file_id: returnedFileId,
          file_name: renamedFileName,
        }
      );

      // Refresh authoritative designs from server
      await fetchOrderDesigns();

      toast(`Uploaded AI file for order ${orderNumber}`, "success");
      setSelectedOrderFile(null);
    } catch (err) {
      clearInterval(progressInterval);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Upload failed";
      toast(errMsg, "error");
    } finally {
      setOrderUploading(false);
      setTimeout(() => {
        setOrderUploadProgress(null);
      }, 1200);
    }
  };

  const handleUploadForRegion = async (item, itemIndex, regionKey, file) => {
    toast(
      "Per-item uploads are deprecated. Use the order-level .ai upload.",
      "warning"
    );
    return;
  };

  const handleDeleteDesign = async (fileId, itemIndex = null) => {
    if (!window.confirm("Are you sure you want to delete this design?")) return;
    try {
      const res = await axios.post(
        `${settings.apiURL}/embroidery_delete_design.php`,
        { fileId },
        { headers: authHeader() }
      );
      if (res.data.success) {
        toast("Design deleted successfully", "success");
        await fetchOrderDesigns();
      } else {
        toast("Failed to delete design", "error");
      }
    } catch (err) {
      toast("Delete failed", "error");
    }
  };

  const formatFileSize = (fileSize) => {
    if (!fileSize && fileSize !== 0) return "N/A";
    if (typeof fileSize === "string") return fileSize;
    const mb = Number(fileSize) / 1024 / 1024;
    if (isNaN(mb)) return "N/A";
    return mb.toFixed(2) + " MB";
  };

  // styles
  const regionButtonBase = {
    minWidth: REGION_MIN_WIDTH,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.08)",
    whiteSpace: "nowrap",
  };

  const oneLineClamp = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  };

  const twoLineClamp = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const modalInnerStyle = {
    maxWidth: MAX_MODAL_WIDTH,
    margin: "0 auto",
    width: "100%",
  };

  const contentBodyStyle = {
    padding: 20,
    maxHeight: "86vh",
    overflowY: "auto",
  };

  // Render uses activeOrder for items & orderNumber
  const items = filteredItems(activeOrder || { items: [] });

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      aria-labelledby="embroidery-upload-modal"
      dialogClassName="embroidery-upload-dialog"
    >
      <div style={modalInnerStyle}>
        <Modal.Header closeButton>
          <Modal.Title id="embroidery-upload-modal">
            Embroidery Upload - Order #
            {(activeOrder || order)?.orderNumber || ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={contentBodyStyle}>
          {(initializing || !isReady) && (
            <div
              style={{
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <Spinner animation="border" variant="primary" />
              <div style={{ fontWeight: "bold" }}>
                Waiting for Google API / Loading...
              </div>
              {initError && (
                <div
                  style={{ color: "red", maxWidth: 900, textAlign: "center" }}
                >
                  {initError}
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" onClick={() => initModal()}>
                      Retry
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isReady && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ width: 220 }}>
                  <Form.Group>
                    <Form.Label>
                      <strong>Select Date</strong>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                    />
                  </Form.Group>
                </div>

                <div style={{ width: 420 }}>
                  <Form.Group>
                    <Form.Label>
                      <strong>Selected Folder</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        selectedFolderName || "No embroidery folder selected"
                      }
                      readOnly
                      style={oneLineClamp}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: 12 }}>
                      Embroidery folder is auto-selected for the chosen date.
                    </Form.Text>
                  </Form.Group>
                </div>

                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontWeight: 600 }}>
                    {truncate((activeOrder || order)?.shipTo?.name || "-", 40)}
                  </div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    {(activeOrder || order)?.createDate
                      ? new Date(
                          (activeOrder || order).createDate
                        ).toLocaleString()
                      : "-"}
                  </div>
                </div>
              </div>

              <Card style={{ marginBottom: 12 }}>
                <Card.Body style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700 }}>Order AI file</div>
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
                    Upload a single <strong>.ai</strong> file for the entire
                    order. This file will be used for all items — per-item
                    uploads are no longer required.
                  </div>

                  <div
                    style={{ display: "flex", gap: 12, alignItems: "center" }}
                  >
                    <input
                      id="file-order-ai"
                      type="file"
                      accept=".ai"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleOrderFileSelected(e.target.files[0] || null)
                      }
                    />

                    <Button
                      size="sm"
                      onClick={() => handleOrderFileClick()}
                      disabled={orderUploading}
                    >
                      {orderUploading
                        ? "Uploading..."
                        : orderFile
                        ? "Replace .ai file"
                        : "Upload .ai file"}
                    </Button>

                    {orderUploadProgress !== null && (
                      <div style={{ width: 160 }}>
                        <ProgressBar
                          now={orderUploadProgress}
                          label={`${orderUploadProgress}%`}
                        />
                      </div>
                    )}

                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      {orderFile ? (
                        <>
                          <div style={{ fontSize: 13 }}>
                            {truncate(orderFile.file_name || "(uploaded)")}
                          </div>
                          <a
                            className="btn btn-sm btn-outline-primary"
                            href={`https://drive.google.com/uc?id=${orderFile.file_id}&export=download`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            📥
                          </a>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              handleDeleteDesign(orderFile.file_id)
                            }
                          >
                            Delete
                          </Button>
                        </>
                      ) : (
                        <div style={{ color: "#999" }}>
                          No AI file uploaded for this order yet.
                        </div>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {items.length === 0 ? (
                <p>No items found in this order.</p>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 14 }}
                >
                  {items.map((item, idx) => {
                    const designsForItem = getDesignsForItem(item, idx);
                    const imageUrl = getItemImageUrl(item);
                    const color =
                      getOptionValue(item, ["color", "colors"]) ||
                      item.color ||
                      "N/A";
                    const size =
                      getOptionValue(item, ["size", "sizes"]) ||
                      item.size ||
                      "N/A";
                    return (
                      <Card key={idx}>
                        <Card.Body>
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              style={{
                                width: ITEM_IMG_SIZE,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={item.name}
                                  style={{
                                    width: ITEM_IMG_SIZE,
                                    height: ITEM_IMG_SIZE,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: ITEM_IMG_SIZE,
                                    height: ITEM_IMG_SIZE,
                                    background: "#eee",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  No Image
                                </div>
                              )}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 12,
                                }}
                              >
                                <div style={{ minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 16,
                                      fontWeight: 700,
                                      marginBottom: 6,
                                      ...twoLineClamp,
                                    }}
                                    title={item.name}
                                  >
                                    {item.name}
                                  </div>
                                  <div style={{ fontSize: 13, color: "#666" }}>
                                    SKU: {item.sku || "-"} • Color: {color} •
                                    Size: {size} • Qty: {item.quantity}
                                  </div>
                                </div>
                              </div>

                              <div style={{ marginTop: 12 }}>
                                {loadingDesigns ? (
                                  <div>Loading designs...</div>
                                ) : null}

                                {orderFile ? (
                                  <div style={{ color: "#333" }}>
                                    <div
                                      style={{ fontSize: 14, fontWeight: 600 }}
                                    >
                                      {truncate(
                                        orderFile.file_name || "(uploaded)"
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: "#999" }}>
                                    No order AI file uploaded
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default EmbroideryUploadModal;
